import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/layout/DashboardLayout';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Funds from './pages/Funds';
import FundForm from './pages/FundForm';
import FundDetails from './pages/FundDetails';
import Investments from './pages/Investments';
import InvestmentForm from './pages/InvestmentForm';
import InvestmentDetails from './pages/InvestmentDetails';
import InvestorContacts from './pages/InvestorContacts';
import InvestorContactForm from './pages/InvestorContactForm';
import InvestorContactDetails from './pages/InvestorContactDetails';
import InvestorAccounts from './pages/InvestorAccounts';
import InvestorAccountForm from './pages/InvestorAccountForm';
import InvestorAccountDetails from './pages/InvestorAccountDetails';
import InvestorReports from './pages/InvestorReports';
import CapitalCallPendency from './pages/reports/CapitalCallPendency';
import AccountTransactionsReport from './pages/reports/AccountTransactionsReport';
import IncompleteProfilesReport from './pages/reports/IncompleteProfilesReport';
import ScheduleK1Report from './pages/reports/ScheduleK1Report';
import Dematerialisation from './pages/demat/Dematerialisation';
import { Toaster } from './components/ui/sonner';
import InvestorCommunication from './pages/InvestorCommunication';
import CommitmentTransferList from './pages/transfers/CommitmentTransferList';
import CommitmentTransferForm from './pages/transfers/CommitmentTransferForm';
import Drawdowns from './pages/drawdowns/Drawdowns';
import DrawdownForm from './pages/drawdowns/DrawdownForm';
import DrawdownDetail from './pages/drawdowns/DrawdownDetail';
import AICertifications from './pages/ai-certification/AICertifications';
import AICertificationForm from './pages/ai-certification/AICertificationForm';
import AICertificationDetail from './pages/ai-certification/AICertificationDetail';
import DataRooms from './pages/data-rooms/DataRooms';
import DataRoomForm from './pages/data-rooms/DataRoomForm';
import DataRoomWorkspace from './pages/data-rooms/DataRoomWorkspace';
import ExternalVDRViewer from './pages/data-rooms/ExternalVDRViewer';
import ComplianceTracker from './pages/ComplianceTracker';
import ComplianceDatabase from './pages/ComplianceDatabase';
import ComplianceReports from './pages/ComplianceReports';
import ComplianceEmailTriggers from './pages/ComplianceEmailTriggers';
import FundCompliance from './pages/FundCompliance';
import DrawdownShortLink from './pages/DrawdownShortLink';
import SmartRedirect from './components/SmartRedirect';
import UserManagement from './pages/UserManagement';
import ComplianceRecipients from './pages/ComplianceRecipients';
import EmailConfiguration from './pages/EmailConfiguration';
import EmailTemplatesList from './pages/email/EmailTemplatesList';
import EmailTemplateForm from './pages/email/EmailTemplateForm';
import WhatsAppConfiguration from './pages/WhatsAppConfiguration';
import WhatsAppTemplatesList from './pages/whatsapp/WhatsAppTemplatesList';
import SentEmails from './pages/SentEmails';
import AuditTrail from './pages/AuditTrail';
import OwnerDashboard from './pages/owner/OwnerDashboard';
import AMCManagement from './pages/owner/AMCManagement';
import AMCDetails from './pages/owner/AMCDetails';
import OwnerTeam from './pages/owner/OwnerTeam';
import OwnerAMCUsers from './pages/owner/OwnerAMCUsers';
import OwnerEmailSettings from './pages/owner/OwnerEmailSettings';
import Reports from './pages/owner/Reports';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/vdr/:token" element={<ExternalVDRViewer />} />
          <Route path="/drawdown/:code" element={<DrawdownShortLink />} />

          {/* Owner Portal Routes - No layout, standalone pages */}
          <Route path="/owner/dashboard" element={<ProtectedRoute requireOwner={true}><OwnerDashboard /></ProtectedRoute>} />
          <Route path="/owner/amcs" element={<ProtectedRoute requireOwner={true}><AMCManagement /></ProtectedRoute>} />
          <Route path="/owner/amcs/:amcId" element={<ProtectedRoute requireOwner={true}><AMCDetails /></ProtectedRoute>} />
          <Route path="/owner/amcs/:amcId/users" element={<ProtectedRoute requireOwner={true}><OwnerAMCUsers /></ProtectedRoute>} />
          <Route path="/owner/users" element={<ProtectedRoute requireOwner={true}><OwnerTeam /></ProtectedRoute>} />
          <Route path="/owner/settings" element={<ProtectedRoute requireOwner={true}><OwnerEmailSettings /></ProtectedRoute>} />
          <Route path="/owner/reports" element={<ProtectedRoute requireOwner={true}><Reports /></ProtectedRoute>} />

          {/* AMC User Protected Routes - With Dashboard Layout */}
          <Route path="/" element={<DashboardLayout />}>
            <Route index element={<SmartRedirect />} />
            <Route path="dashboard" element={<ProtectedRoute module="dashboard"><Dashboard /></ProtectedRoute>} />
            
            {/* Funds Routes */}
            <Route path="funds" element={<ProtectedRoute module="funds"><Funds /></ProtectedRoute>} />
            <Route path="funds/create" element={<ProtectedRoute module="funds"><FundForm /></ProtectedRoute>} />
            <Route path="funds/:id" element={<ProtectedRoute module="funds"><FundDetails /></ProtectedRoute>} />
            <Route path="funds/:id/edit" element={<ProtectedRoute module="funds"><FundForm /></ProtectedRoute>} />
            
            {/* Investments Routes */}
            <Route path="investments" element={<ProtectedRoute module="investments"><Investments /></ProtectedRoute>} />
            <Route path="investments/create" element={<ProtectedRoute module="investments"><InvestmentForm /></ProtectedRoute>} />
            <Route path="investments/:id" element={<ProtectedRoute module="investments"><InvestmentDetails /></ProtectedRoute>} />
            
            {/* Investors Routes - Submodule-level protection */}
            <Route path="investors/contacts" element={<ProtectedRoute module="investor_contacts"><InvestorContacts /></ProtectedRoute>} />
            <Route path="investors/contacts/create" element={<ProtectedRoute module="investor_contacts"><InvestorContactForm /></ProtectedRoute>} />
            <Route path="investors/contacts/:id" element={<ProtectedRoute module="investor_contacts"><InvestorContactDetails /></ProtectedRoute>} />
            <Route path="investors/contacts/:id/edit" element={<ProtectedRoute module="investor_contacts"><InvestorContactForm /></ProtectedRoute>} />
            <Route path="investors/accounts" element={<ProtectedRoute module="investor_accounts"><InvestorAccounts /></ProtectedRoute>} />
            <Route path="investors/accounts/create" element={<ProtectedRoute module="investor_accounts"><InvestorAccountForm /></ProtectedRoute>} />
            <Route path="investors/accounts/:id" element={<ProtectedRoute module="investor_accounts"><InvestorAccountDetails /></ProtectedRoute>} />
            <Route path="investors/accounts/:id/edit" element={<ProtectedRoute module="investor_accounts"><InvestorAccountForm /></ProtectedRoute>} />
            <Route path="investors/reports" element={<ProtectedRoute module="investor_reports"><InvestorReports /></ProtectedRoute>} />
            <Route path="investors/reports/capital-call-pendency" element={<ProtectedRoute module="investor_reports"><CapitalCallPendency /></ProtectedRoute>} />
            <Route path="investors/reports/account-transactions" element={<ProtectedRoute module="investor_reports"><AccountTransactionsReport /></ProtectedRoute>} />
            <Route path="investors/reports/incomplete-profiles" element={<ProtectedRoute module="investor_reports"><IncompleteProfilesReport /></ProtectedRoute>} />
            <Route path="investors/reports/schedule-k1" element={<ProtectedRoute module="investor_reports"><ScheduleK1Report /></ProtectedRoute>} />
            <Route path="investors/dematerialisation" element={<ProtectedRoute module="investor_accounts"><Dematerialisation /></ProtectedRoute>} />
            <Route path="investors/communication" element={<ProtectedRoute module="investor_accounts"><InvestorCommunication /></ProtectedRoute>} />
            <Route path="investors/transfers" element={<ProtectedRoute module="investor_accounts"><CommitmentTransferList /></ProtectedRoute>} />
            <Route path="investors/transfers/new" element={<ProtectedRoute module="investor_accounts"><CommitmentTransferForm /></ProtectedRoute>} />
            <Route path="investors/transfers/:id" element={<ProtectedRoute module="investor_accounts"><CommitmentTransferForm /></ProtectedRoute>} />
            
            {/* Drawdown Management Routes */}
            <Route path="drawdowns" element={<ProtectedRoute module="drawdowns"><Drawdowns /></ProtectedRoute>} />
            <Route path="drawdowns/create" element={<ProtectedRoute module="drawdowns"><DrawdownForm /></ProtectedRoute>} />
            <Route path="drawdowns/:id" element={<ProtectedRoute module="drawdowns"><DrawdownDetail /></ProtectedRoute>} />
            
            {/* AI Certification Routes */}
            <Route path="ai-certification" element={<ProtectedRoute module="ai_certification"><AICertifications /></ProtectedRoute>} />
            <Route path="ai-certification/create" element={<ProtectedRoute module="ai_certification"><AICertificationForm /></ProtectedRoute>} />
            <Route path="ai-certification/:id" element={<ProtectedRoute module="ai_certification"><AICertificationDetail /></ProtectedRoute>} />
            <Route path="ai-certification/:id/edit" element={<ProtectedRoute module="ai_certification"><AICertificationForm /></ProtectedRoute>} />

            {/* Data Room Routes */}
            <Route path="data-rooms" element={<ProtectedRoute module="data_rooms"><DataRooms /></ProtectedRoute>} />
            <Route path="data-rooms/create" element={<ProtectedRoute module="data_rooms"><DataRoomForm /></ProtectedRoute>} />
            <Route path="data-rooms/:id" element={<ProtectedRoute module="data_rooms"><DataRoomWorkspace /></ProtectedRoute>} />

            {/* Compliance Tracker */}
            <Route path="compliance" element={<ProtectedRoute module="compliance"><ComplianceTracker /></ProtectedRoute>} />
            <Route path="compliance/database" element={<ProtectedRoute module="compliance"><ComplianceDatabase /></ProtectedRoute>} />
            <Route path="compliance/reports" element={<ProtectedRoute module="compliance"><ComplianceReports /></ProtectedRoute>} />
            <Route path="compliance/email-triggers" element={<ProtectedRoute module="compliance"><ComplianceEmailTriggers /></ProtectedRoute>} />
            <Route path="funds/:fundId/compliance" element={<ProtectedRoute module="compliance"><FundCompliance /></ProtectedRoute>} />

            {/* User Management Routes - Require admin role */}
            <Route path="users" element={<ProtectedRoute requireAdmin={true}><UserManagement /></ProtectedRoute>} />
            <Route path="compliance-recipients" element={<ProtectedRoute requireAdmin={true}><ComplianceRecipients /></ProtectedRoute>} />
            <Route path="audit-trail" element={<ProtectedRoute requireAdmin={true}><AuditTrail /></ProtectedRoute>} />
            <Route path="email-config" element={<ProtectedRoute><EmailConfiguration /></ProtectedRoute>} />
            <Route path="email-templates" element={<ProtectedRoute><EmailTemplatesList /></ProtectedRoute>} />
            <Route path="email-templates/new" element={<ProtectedRoute><EmailTemplateForm /></ProtectedRoute>} />
            <Route path="email-templates/:id" element={<ProtectedRoute><EmailTemplateForm /></ProtectedRoute>} />
            <Route path="email-templates/:id/edit" element={<ProtectedRoute><EmailTemplateForm /></ProtectedRoute>} />
            <Route path="whatsapp-config" element={<ProtectedRoute><WhatsAppConfiguration /></ProtectedRoute>} />
            <Route path="whatsapp-templates" element={<ProtectedRoute><WhatsAppTemplatesList /></ProtectedRoute>} />
            <Route path="sent-emails" element={<ProtectedRoute><SentEmails /></ProtectedRoute>} />
          </Route>

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
